// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DashboardDesignerBody.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from "react";
import ReactDOM from "react-dom";
import m from "mobx";
import classnames from "classnames";
import connect from "share/enhancers/Izenda.Connect";
import DashboardSubscribe from "modules/Dashboard/DashboardSubscribe/Izenda.DashboardSubscribe";
import ReportViewerFilters from "modules/Reports/ReportViewer/Izenda.ReportViewerFilters";
import DashboardGrid from "./DashboardGrid/Izenda.DashboardGrid";
import DashboardPresentation from "./DashboardPresentation/Izenda.DashboardPresentation";
import Dimensions from "share/components/hoc/Izenda.Dimensions";
import reportViewerUIStoreFactory from "modules/Reports/ReportViewer/Izenda.ReportViewerUIStoreFactory";
import DashboardFullscreen from "./DashboardPresentation/Izenda.DashboardFullscreen";
import DashboardTile from "./DashboardGrid/DashboardTile/Izenda.DashboardTile";
import CONSTANT from "logic/data/Izenda.AccessConstants";
import AccessGrid from "modules/Access/Izenda.AccessGrid";
import UiState from "logic/Izenda.UiState";
import IntegrationStyleAction from "pages/Izenda.IntegrationStyleAction";

const commonFilterAction = IntegrationStyleAction.checkDisplayByKey(
  "hideCommonFilter"
);
const CommonFilter = commonFilterAction(ReportViewerFilters);

@connect(Dimensions)
class DashboardDesignerBody extends Component {
  static propTypes = {
    store: PropTypes.object
  };
  static displayName = "DashboardDesignerBody";

  constructor() {
    super();

    this.reportViewerUIStore = {};
  }

  componentDidMount() {
    this.handleFreezeSection();
    this.updateView();
  }

  componentWillUnmount() {
    this.doesFreezeDisposer();
  }

  /**
     * Handle freeze section
     */
  handleFreezeSection = () => {
    this.reportViewerUIStore = {};
    reportViewerUIStoreFactory(this.reportViewerUIStore);

    this.doesFreezeDisposer = m.observe(
      this.reportViewerUIStore,
      "doesFreeze",
      () => {
        if (!this.reportViewerUIStore.doesFreeze) {
          $izenda(".content-container").scrollTop(0);
        }
      }
    );
  };

  /**
   * Update layout after resized filter panel.
   */
  updateView = () => {
    let viewerContentNode = ReactDOM.findDOMNode(this.refs.viewerContent);
    this.reportViewerUIStore.viewerContentTop =
      viewerContentNode && viewerContentNode.getBoundingClientRect().top;
  };

  /**
   * Handle on remove pill
   */
  onRemovingPillHandler = (...args) => {
    let store = this.props.store;
    return store.onRemovingPillHandler.call(store, ...args);
  };

  /**
   * Handle on remove all pill
   */
  onRemovingAllPillHandler = (...args) => {
    let store = this.props.store;
    return store.onRemovingAllPillHandler.call(store, ...args);
  };

  /**
   * render designer component
   * @return {Object} designer component
   */
  renderDesigner = () => {
    let store = this.props.store;
    let {
      dashboardDetail,
      dashboardDetail: { dashboard: { crossFilteringDetails, accessPriority } },
      dashboardFilterStore: {
        model: { reportFilter: { displayConfigFields: configFields } }
      }
    } = store;

    let contentContainerClassName = classnames("Izenda-DashboardGrid_Section", {
      scrollable: this.reportViewerUIStore.doesFreeze
    });

    let dashboardDesignerContentClassName = classnames(
      "izenda-DashboardDesignerContent",
      {
        u_none:
          !store.dashboardDesignerMode.isDesigner ||
          !!dashboardDetail.dashboard.fullScreenDashboardPart
      }
    );

    let dashboardDesignerContainerClassName = classnames(
      "u_noMarginAll u_noPaddingAll u_fullHeight",
      {
        u_none: !store.dashboardDesignerMode.isDesigner
      }
    );

    return (
      <div className={dashboardDesignerContainerClassName}>
        {dashboardDetail.dashboard.fullScreenDashboardPart &&
          <DashboardTile
            {...this.props}
            dashboardPart={dashboardDetail.dashboard.fullScreenDashboardPart}
            dashboardDetail={dashboardDetail}
            viewMode={false}
          />}
        <div className={dashboardDesignerContentClassName}>
          <CommonFilter
            accessPriority={accessPriority}
            configFields={configFields}
            uiStore={this.reportViewerUIStore}
            fieldStore={store.dashboardFilterStore}
            haveFreeze={!UiState.isMobile}
            crossFilteringDetails={crossFilteringDetails}
            removeCrossFiltering={this.onRemovingPillHandler}
            removeAllCrossFiltering={this.onRemovingAllPillHandler}
            showCrossFilteringOption={false}
            hadSetting={false}
            enableDeleteAll
            isLazyLoading
          />

          <DashboardGrid
            ref="viewerContent"
            className={contentContainerClassName}
            {...this.props}
            dashboardDetail={dashboardDetail}
            reportViewerUIStore={this.reportViewerUIStore}
            style={{ height: this.reportViewerUIStore.viewerContentHeight }}
          />
        </div>
      </div>
    );
  };

  /**
   * render presentation component
   * @return {Object} presentation component
   */
  renderPresentation = () => {
    return this.props.store.dashboardDesignerMode.isPresentation
      ? <DashboardPresentation {...this.props} />
      : null;
  };

  /**
   * render presentation component
   * @return {Object} presentation component
   */
  renderFullscreen = () => {
    return this.props.store.dashboardDesignerMode.isFullscreen
      ? <DashboardFullscreen {...this.props} />
      : null;
  };

  /**
   * render subscribe component
   * @return {Object} subscribe component
   */
  renderSubscribe = () => {
    return this.props.store.dashboardDesignerMode.isSubscribe
      ? <DashboardSubscribe {...this.props} />
      : null;
  };

  /**
   * render scheduling component
   * @return {Object} scheduling component
   */
  renderScheduling = () => {
    return this.props.store.dashboardDesignerMode.isSchedule
      ? <DashboardSubscribe {...this.props} />
      : null;
  };

  /**
   * render access component
   * @return {Object} access component
   */
  renderAccess = () => {
    var store = this.props.store;
    return store.dashboardDesignerMode.isAccess
      ? <AccessGrid
          isDashboard
          dashboardId={store.dashboardDetail.dashboard.id}
          isGlobal={store.dashboardDetail.dashboard.isGlobal}
          store={store.accessStore}
          users={store.accessStore.users}
          roles={store.accessStore.roles}
          tableDef={CONSTANT.ACCESS_TABLE_DEF}
          searchOptions={CONSTANT.SEARCH_OPTIONS}
          accessMode={CONSTANT.ACCESS_MODE.DASHBOARD}
        />
      : null;
  };

  render() {
    return (
      <div className="izenda-DashboardDesignerBody u_fullHeight">
        {this.renderDesigner()}
        {this.renderPresentation()}
        {this.renderSubscribe()}
        {this.renderScheduling()}
        {this.renderFullscreen()}
        {this.renderAccess()}
      </div>
    );
  }
}

export default DashboardDesignerBody;



// WEBPACK FOOTER //
// ./ui/modules/Dashboard/DashboardDesigner/Izenda.DashboardDesignerBody.js